/**
 * Copyright (c) 2024-2026 Danielle Sage. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

export default function DirectorReview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [decidedBy, setDecidedBy] = useState("");
  const [verifications, setVerifications] = useState([]);
  const [walletScreenings, setWalletScreenings] = useState([]);
  const [sowSessions, setSowSessions] = useState([]);
  const [sowDocs, setSowDocs] = useState({});
  const [expandedSow, setExpandedSow] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setError("Invalid review link."); setLoading(false); return; }
    loadReview(token);
  }, []);

  const loadReview = async (token) => {
    try {
      const { data: reviews, error: re } = await supabase.from("director_reviews").select("*").eq("token", token).single();
      if (re || !reviews) { setError("Review not found or link has expired."); setLoading(false); return; }
      if (reviews.status === "superseded") {
        setError("This review link has been superseded. Sage Capital Management has sent you an updated link — please use the most recent email.");
        setLoading(false);
        return;
      }
      if (reviews.status !== "pending") {
        setReview(reviews);
        setSubmitted(true);
        setLoading(false);
        return;
      }
      if (new Date(reviews.expires_at) < new Date()) { setError("This review link has expired. Please contact Sage Capital Management."); setLoading(false); return; }
      setReview(reviews);

      const { data: sess } = await supabase.from("onboarding_sessions").select("*").eq("id", reviews.session_id).single();
      setSession(sess);

      const { data: docs } = await supabase.from("onboarding_documents").select("*").eq("session_id", reviews.session_id).order("created_at");
      setDocuments(docs || []);
      const { data: verifs } = await supabase.from("sumsub_verifications").select("*").eq("session_id", reviews.session_id);
      setVerifications(verifs || []);
      const { data: wallets } = await supabase.from("wallet_screenings").select("*").eq("session_id", reviews.session_id);
      setWalletScreenings(wallets || []);
      // Build AML checklist from session data
      setSowSessions(prev => prev); // trigger after session loaded

      // Fetch SOW sessions for UBOs
      const { data: sowData } = await supabase.from("onboarding_sessions")
        .select("*")
        .in("status", ["sow_complete", "sow_interview"])
        .order("created_at");
      // Filter to those linked to this parent session
      const linkedSow = (sowData || []).filter(s => 
        s.collected_data?.parentSessionId === reviews.session_id ||
        (session?.contact_email && s.contact_email?.toLowerCase() === session.contact_email?.toLowerCase())
      );
      setSowSessions(linkedSow);
      // Fetch SOW documents for each
      for (const sow of linkedSow) {
        const { data: sd } = await supabase.from("onboarding_documents").select("*").eq("session_id", sow.id).order("created_at");
        setSowDocs(prev => ({ ...prev, [sow.id]: sd || [] }));
      }

    } catch(e) {
      setError("Unable to load review: " + e.message);
    }
    setLoading(false);
  };

  const submitDecision = async (dec) => {
    if (dec === "info" && !notes.trim()) { alert("Please add notes for Ross explaining what needs to be addressed."); return; }
    setSubmitting(true);
    try {
      const res = await fetch("/api/director-decision", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: review.token, decision: dec, notes, decidedBy: "Nathan Sage (Director)" })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDecision(dec);
      setSubmitted(true);
    } catch(e) {
      alert("Error: " + e.message);
    }
    setSubmitting(false);
  };

  const getSignedUrl = async (filePath) => {
    const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(filePath, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const S = {
    page: { minHeight: "100vh", background: "#f8fafc", fontFamily: "Georgia, serif", color: "#111827" },
    header: { background: "#1e2d5a", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    container: { maxWidth: 900, margin: "0 auto", padding: "32px 24px" },
    section: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "24px 28px", marginBottom: 20 },
    sectionTitle: { fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16, paddingBottom: 8, borderBottom: "2px solid #1e2d5a" },
    kv: { display: "flex", gap: 8, marginBottom: 8 },
    kvLabel: { fontSize: 12, color: "#6b7280", minWidth: 140, flexShrink: 0, fontFamily: "Arial, sans-serif" },
    kvVal: { fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.5 },
    para: { fontSize: 13, color: "#374151", lineHeight: 1.8, fontFamily: "Arial, sans-serif", marginBottom: 8 },
    badge: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: color + "20", border: "1px solid " + color + "40", color }),
    docRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f3f4f6" },
    btn: (bg, color) => ({ padding: "12px 28px", background: bg, color, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" }),
  };

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: "#6b7280" }}>Loading review file...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>Please contact the compliance team at compliance@sagecapital.co.uk</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 500, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{review?.status === "approved" || decision === "approve" ? "✅" : review?.status === "declined" || decision === "decline" ? "❌" : "📋"}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d5a", marginBottom: 8 }}>
          {decision === "approve" || review?.status === "approved" ? "Sent to Jaybee" : "Returned to Ross"}
        </div>
        <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          {decision === "approve" ? "Thank you. The file has been sent to Jaybee Compliance for external sign-off. Ross has been notified." :
           decision === "info" ? "Thank you. The file has been returned to Ross with your notes. He will follow up accordingly." :
           "This review has already been completed."}
        </div>
        {review?.decision_notes && <div style={{ marginTop: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 8, fontSize: 13, color: "#374151", textAlign: "left" }}><strong>Your notes:</strong> {review.decision_notes}</div>}
      </div>
    </div>
  );

  // Safely coerce any report array item to a string.
  // The AI occasionally wraps string values in objects e.g. {note:"..."} — this guards all renders.
  const toStr = v => {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") return v.note || v.text || v.content || v.value || v.flag || v.finding || JSON.stringify(v);
    return String(v);
  };

  const report = session?.report || {};
  const cd = report.companyDetails || {};
  const fc = report.finsaClassification || {};
  const finsaCategory = typeof fc === "object" ? (fc.category || "—") : String(fc || "—");
  const finsaBasis = typeof fc === "object" ? fc.basis : null;
  const riskColor = (report.riskTier || "").toLowerCase().includes("high") ? "#dc2626" : (report.riskTier || "").toLowerCase().includes("medium") ? "#d97706" : "#16a34a";

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 11, color: "#93c5fd", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>Sage Capital Management</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>Client Acceptance — Director Review</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#93c5fd" }}>Confidential — Director only</div>
          <div style={{ fontSize: 11, color: "#93c5fd" }}>Expires: {review?.expires_at ? new Date(review.expires_at).toLocaleDateString("en-GB") : "—"}</div>
        </div>
      </div>

      <div style={S.container}>
        {/* Recommendation banner */}
        <div style={{ background: "#f0fdf4", border: "2px solid #16a34a", borderRadius: 8, padding: "16px 24px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif" }}>Internal MLRO Recommendation</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#15803d" }}>APPROVE</div>
          </div>
          <div style={{ textAlign: "right", fontFamily: "Arial, sans-serif" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Reviewed by</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Ross Griffiths, Compliance Manager</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB") : "—"}</div>
          </div>
        </div>

        {/* MLRO conditions */}
        {session?.mlro_notes && (
          <div style={{ ...S.section, borderLeft: "4px solid #1e2d5a" }}>
            <div style={S.sectionTitle}>MLRO Conditions & Notes</div>
            <div style={S.para}>{session.mlro_notes}</div>
          </div>
        )}

        {/* Website Pre-Screening */}
        {(report.websiteFindings?.url && report.websiteFindings?.summary && report.websiteFindings.summary !== "...") && (
          ((report.websiteFindings.redFlags || []).filter(f => f && f !== "...").length > 0 ||
           (report.websiteFindings.contradictions || []).filter(c => c && c !== "...").length > 0) && (
          <div style={{ ...S.section, borderLeft: "4px solid #dc2626" }}>
            <div style={S.sectionTitle}>Website Pre-Screening</div>
            <div style={{ ...S.para, marginBottom:10 }}>{toStr(report.websiteFindings.summary)}</div>
            {(report.websiteFindings.redFlags || []).filter(f => f && f !== "...").map((f, i) => (
              <div key={i} style={{ fontSize:12, color:"#dc2626", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>🚩 {toStr(f)}</div>
            ))}
            {(report.websiteFindings.contradictions || []).filter(c => c && c !== "...").map((c, i) => (
              <div key={i} style={{ fontSize:12, color:"#d97706", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>⚠ {toStr(c)}</div>
            ))}
            {report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"10px 14px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:6 }}>
                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ MLRO Mitigation Note</div>
                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{toStr(report.websiteFindings.mlroNote)}</div>
              </div>
            )}
            {!report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"8px 12px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6, fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif" }}>
                ⚠ No MLRO mitigation note recorded for this website discrepancy.
              </div>
            )}
          </div>
          )
        )}

        {/* AML Compliance Summary Table */}
        {session && (() => {
          const r = report;
          const sumsubOk = verifications.some(v => v.status === "approved");
          const allDocsOk = documents.length > 0 && documents.every(d => d.assessment !== "Unsatisfactory");
          const checks = [
            { label: "Contracting party identified", ok: sumsubOk, note: sumsubOk ? "Sumsub verified" : "Verification pending" },
            { label: "Beneficial ownership established", ok: !!(r.ubos?.length > 0), note: r.ubos?.length > 0 ? `${r.ubos.length} UBO(s) documented` : "No UBOs recorded" },
            { label: "Tax compliance declaration", ok: !!(r.taxCompliance?.declared), note: r.taxCompliance?.declared ? "Declaration obtained from client" : r.taxCompliance?.riskIndicatorsPresent ? "⚠ Risk indicators present" : "Not collected" },
            { label: "Risk classification", ok: !!(r.riskTier), note: r.riskTier ? `${r.riskTier} risk — rationale documented` : "Not assigned" },
            { label: "PEP / sanctions screening", ok: sumsubOk, note: sumsubOk ? "Clear — Sumsub" : "Pending" },
            { label: "FATF jurisdiction assessment", ok: !r.fatfFlags || !r.fatfFlags?.entityJurisdictionFlagged, note: !r.fatfFlags ? "Pre-dates FATF check — manually verified" : r.fatfFlags?.entityJurisdictionFlagged ? `${r.fatfFlags.fatfStatus || "Flagged"} — EDD applied` : "Clean — no flags" },
            { label: "Source of funds", ok: !!(r.sofAssessment?.narrative || r.sofAssessment?.fundingRoute), note: r.sofAssessment?.fundingRoute || r.sofAssessment?.narrative ? "Documented" : "Not documented" },
            { label: "Source of wealth (UBOs 25%+)", ok: !!r.sowWaiver || !r.ubos?.some(u => parseFloat(u.ownership) >= 25) || r.ubos?.filter(u => parseFloat(u.ownership) >= 25).every(u => u.sowAssessment), note: r.sowWaiver ? `Not required — MLRO waiver: ${r.sowWaiver.reason || ""}` : !r.ubos?.some(u => parseFloat(u.ownership) >= 25) ? "No qualifying UBOs" : r.ubos.filter(u => parseFloat(u.ownership) >= 25).every(u => u.sowAssessment) ? "All UBOs interviewed & assessed" : "Some UBOs pending" },
            (() => {
              const hasW = (session?.collected_data?.walletAddresses || []).length > 0 || walletScreenings.length > 0;
              const screened = !hasW || walletScreenings.length > 0;
              return { label: "Chainalysis wallet screening", ok: screened, note: !hasW ? "No wallets provided" : screened ? `${walletScreenings.length} wallet(s) screened` : "Wallets provided but not yet screened" };
            })(),
            (() => {
              const tr = report.travelRule;
              if (!tr || tr.overallStatus === "not_applicable" || walletScreenings.length === 0) return { label: "Travel Rule (FINMA 02/2019)", ok: true, note: "No crypto wallets — not applicable" };
              return { label: "Travel Rule (FINMA 02/2019)", ok: tr.overallStatus === "compliant", note: tr.overallStatus === "compliant" ? "All wallets verified" : `${(tr.wallets||[]).filter(w=>w.travelRuleStatus!=="compliant").length} wallet(s) pending verification` };
            })(),
            { label: "Documents on file", ok: allDocsOk, note: `${documents.length} document(s) — ${allDocsOk ? "all satisfactory" : "review required"}` },
            { label: "MLRO internal sign-off", ok: !!(session.status === "approved" || session.status === "pending_external"), note: "Approved by Compliance Manager" },
          ];
          const allOk = checks.every(c => c.ok);
          return (
            <div style={{ ...S.section, marginBottom: 20 }}>
              <div style={S.sectionTitle}>AML Compliance Summary</div>
              <div style={{ ...S.para, marginBottom: 14 }}>
                The following table summarises Sage Capital Management's completion of each AML/KYC requirement under the Swiss VQF regulatory framework and the company's AML policy. Each item has been completed and documented prior to this submission.
              </div>
              <div style={{ background: allOk ? "#f0fdf4" : "#fff7ed", border: `1px solid ${allOk ? "#86efac" : "#fed7aa"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: allOk ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif" }}>
                  {allOk ? "✓ All AML requirements met — file is complete" : "⚠ Some items require attention — see details below"}
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <thead>
                  <tr style={{ background: "#1e2d5a" }}>
                    <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>AML Requirement</th>
                    <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 60 }}>Status</th>
                    <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif" }}>{c.label}</td>
                      <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                      <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#6b7280" : "#d97706", fontFamily: "Arial, sans-serif" }}>{c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Client Profile */}
        <div style={S.section}>
          <div style={S.sectionTitle}>1. Client Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {[
              ["Legal Name", cd.legalName || session?.company_name],
              ["Jurisdiction", cd.jurisdiction || report.jurisdiction],
              ["Registration No.", cd.registrationNumber || report.registrationNumber],
              ["Incorporated", cd.dateOfIncorporation],
              ["Registered Address", cd.registeredAddress || report.registeredAddress],
              ["Business", cd.businessDescription || report.businessDescription],
            ].filter(([,v]) => v).map(([label, val]) => (
              <div key={label} style={S.kv}>
                <span style={S.kvLabel}>{label}</span>
                <span style={S.kvVal}>{toStr(val)}</span>
              </div>
            ))}
          </div>
          {(cd.groupStructure || report.groupStructure) && <div style={{ marginTop: 12, ...S.para }}><strong>Group structure:</strong> {toStr(cd.groupStructure || report.groupStructure)}</div>}
        </div>

        {/* Risk & FinSA */}
        <div style={S.section}>
          <div style={S.sectionTitle}>2. Risk Assessment & FinSA Classification</div>
          <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Risk Tier</div>
              <span style={S.badge(riskColor)}>{report.riskTier || "—"}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>FinSA Classification</div>
              <span style={S.badge("#1e2d5a")}>{finsaCategory}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Review Period</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{report.reviewPeriod || "—"}</div>
            </div>
          </div>
          {finsaBasis && <div style={S.para}><strong>FinSA basis:</strong> {toStr(finsaBasis)}</div>}
          {(typeof fc === "object" && fc.assessment) && <div style={S.para}>{toStr(fc.assessment)}</div>}
          {report.riskRationale && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.riskRationale)}</div>}
        </div>

        {/* Beneficial Ownership */}
        <div style={S.section}>
          <div style={S.sectionTitle}>3. Beneficial Ownership & Identity Verification</div>
          {(report.ubos || []).map((ubo, i) => {
            const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase()));
            return (
              <div key={i} style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: 8, marginBottom: 10, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}{ubo.countryOfResidence ? " · Resident: " + ubo.countryOfResidence : ""}</div>
                  </div>
                  <span style={S.badge(ubo.corporateUBO ? "#7c3aed" : "#1e2d5a")}>{ubo.corporateUBO ? "Corporate UBO" : "Individual UBO"}</span>
                </div>
                {/* Identity verification */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>ID Verification (Sumsub)</div>
                    {verifMatch ? (
                      <div>
                        <span style={S.badge(verifMatch.status === "approved" ? "#15803d" : verifMatch.status === "pending" ? "#d97706" : "#dc2626")}>
                          {verifMatch.status === "approved" ? "✓ Verified" : verifMatch.status === "pending" ? "⏳ Pending" : "✗ " + verifMatch.status}
                        </span>
                        {verifMatch.applicant_id && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 4 }}>Ref: {verifMatch.applicant_id}</div>}
                      </div>
                    ) : (
                      <span style={S.badge("#9ca3af")}>Pending</span>
                    )}
                  </div>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>PEP / Sanctions</div>
                    <span style={S.badge("#15803d")}>No match</span>
                  </div>
                  {ubo.controlBeyondOwnership !== undefined && (
                    <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Control beyond ownership</div>
                      <span style={S.badge(ubo.controlBeyondOwnership ? "#d97706" : "#15803d")}>{ubo.controlBeyondOwnership ? "Yes" : "No"}</span>
                      {ubo.controlNotes && <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 4 }}>{toStr(ubo.controlNotes)}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(report.directors || []).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Directors / Authorised Persons</div>
              {report.directors.map((d, i) => {
                const name = typeof d === "object" ? d.name : d;
                const role = typeof d === "object" ? d.role : "Director";
                const verifD = verifications.find(v => v.person_name?.toLowerCase().includes(name?.split(" ")[0]?.toLowerCase()));
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{name}</span>
                      <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{role}</span>
                    </div>
                    {verifD ? <span style={S.badge(verifD.status === "approved" ? "#15803d" : "#d97706")}>{verifD.status === "approved" ? "✓ Verified" : "⏳ Pending"}</span> : <span style={S.badge("#9ca3af")}>Pending</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Source of Funds */}
        {report.sofAssessment && (
          <div style={S.section}>
            <div style={S.sectionTitle}>4. Source of Funds</div>
            {report.sofAssessment.fundingRoute && <div style={S.kv}><span style={S.kvLabel}>Funding Route</span><span style={S.kvVal}>{report.sofAssessment.fundingRoute}</span></div>}
            {report.sofAssessment.expectedVolumes && <div style={S.kv}><span style={S.kvLabel}>Expected Volumes</span><span style={S.kvVal}>{report.sofAssessment.expectedVolumes}</span></div>}
            {report.sofAssessment.assessment && <div style={{ ...S.para, marginTop: 8 }}>{report.sofAssessment.assessment}</div>}
          </div>
        )}

        {/* Source of Wealth — unified section 5 */}
        {((report.ubos || []).some(u => parseFloat(u.ownership) >= 25) || sowSessions.length > 0) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>5. Source of Wealth — UBOs ≥ 25%</div>
            <div style={{ ...S.para, color: "#6b7280", marginBottom: 16 }}>
              Source of wealth profiles were collected via a structured compliance interview. Each profile was reviewed and signed off by the internal MLRO.
            </div>
            {(report.ubos || []).filter(u => parseFloat(u.ownership) >= 25).map((ubo, i) => {
              const sowSess = sowSessions.find(s => 
                s.contact_email?.toLowerCase() === ubo.email?.toLowerCase() || 
                s.contact_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase())
              );
              const sowReport = sowSess?.report || {};
              const isExpanded = expandedSow[ubo.name] || false;
              const assessment = ubo.sowAssessment || sowReport.sowAssessment;
              return (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#f9fafb", cursor: "pointer" }}
                    onClick={() => setExpandedSow(prev => ({ ...prev, [ubo.name]: !prev[ubo.name] }))}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {assessment && <span style={S.badge(assessment.includes("Satisfactory") ? "#15803d" : assessment.includes("Basic") || assessment.includes("Clarification") ? "#d97706" : "#dc2626")}>{assessment}</span>}
                      <span style={{ fontSize: 16, color: "#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "16px 20px" }}>
                      {/* MLRO summary narrative */}
                      {ubo.sowNarrative && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 6, fontWeight: 700 }}>MLRO Summary</div>
                          <div style={S.para}>{toStr(ubo.sowNarrative)}</div>
                          {ubo.sowMlroRationale && <div style={{ ...S.para, fontStyle: "italic", color: "#6b7280", marginTop: 4, paddingLeft: 12, borderLeft: "3px solid #e5e7eb" }}>{toStr(ubo.sowMlroRationale)}</div>}
                        </div>
                      )}
                      {/* Full interview detail */}
                      {sowSess && (
                        <div>
                          {sowReport.professionalBackground && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Professional Background</div>
                              <div style={S.para}>{toStr(sowReport.professionalBackground)}</div>
                            </div>
                          )}
                          {sowReport.shareholdingOrigin && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Shareholding Origin</div>
                              <div style={S.para}>{toStr(sowReport.shareholdingOrigin)}</div>
                            </div>
                          )}
                          {(sowReport.sowNotes || sowReport.wealthOrigins) && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Wealth Origins</div>
                              <div style={S.para}>{toStr(sowReport.sowNotes || sowReport.wealthOrigins)}</div>
                            </div>
                          )}
                          {sowReport.significantEvents && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Significant Events</div>
                              <div style={S.para}>{toStr(sowReport.significantEvents)}</div>
                            </div>
                          )}
                          {[["Wealth timeframe", sowReport.wealthTimeframe], ["Wealth structure", sowReport.wealthStructure]].filter(([,v]) => v).map(([label, val]) => (
                            <div key={label} style={S.kv}><span style={S.kvLabel}>{label}</span><span style={S.kvVal}>{toStr(val)}</span></div>
                          ))}
                        </div>
                      )}
                      {/* Evidence */}
                      {(ubo.evidenceProvided || sowReport.evidenceProvided) && (
                        <div style={{ padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, margin: "12px 0" }}>
                          <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Evidence Provided</div>
                          <div style={{ fontSize: 13, color: "#0c4a6e", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{toStr(ubo.evidenceProvided || sowReport.evidenceProvided)}</div>
                        </div>
                      )}
                      {/* SOW documents */}
                      {sowSess && (sowDocs[sowSess.id] || []).length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 8, fontWeight: 700 }}>Supporting Documents</div>
                          {sowDocs[sowSess.id].map((doc, di) => (
                            <div key={di} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f9fafb", borderRadius: 6, marginBottom: 6, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 18 }}>📄</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{doc.document_name}</div>
                                {doc.assessment && <div style={{ fontSize: 11, color: doc.assessment === "Satisfactory" ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif", marginTop: 2, fontWeight: 600 }}>{doc.assessment}</div>}
                              </div>
                              {doc.file_path ? (
                                <button onClick={async () => {
                                  const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(doc.file_path, 3600);
                                  if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                                }} style={{ padding: "6px 14px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View ↗</button>
                              ) : <span style={{ fontSize: 11, color: "#d1d5db" }}>No file</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {sowSess?.updated_at && <div style={{ marginTop: 10, fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>Interview completed: {new Date(sowSess.updated_at).toLocaleDateString("en-GB")}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

                {/* Tax Compliance */}
        <div style={S.section}>
          <div style={S.sectionTitle}>6b. Tax Compliance Declaration</div>
          {report.taxCompliance?.riskIndicatorsPresent ? (
            <div style={{ padding:"10px 14px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#d97706", fontFamily:"Arial, sans-serif", marginBottom:4 }}>⚠ Risk Indicators Present</div>
              <div style={{ fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{toStr(report.taxCompliance.notes)}</div>
              {(report.taxCompliance.riskIndicators || []).map((r,i) => <div key={i} style={{ fontSize:11, color:"#92400e", fontFamily:"Arial, sans-serif" }}>• {toStr(r)}</div>)}
            </div>
          ) : report.taxCompliance?.declared ? (
            <div style={{ padding:"10px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#15803d", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ Declaration Obtained</div>
              <div style={{ fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.taxCompliance.declarationText || "Client confirmed assets are fully tax compliant in all relevant jurisdictions."}</div>
            </div>
          ) : (
            <div style={{ ...S.para, color:"#9ca3af" }}>Tax compliance declaration not yet recorded.</div>
          )}
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginTop:8 }}>Required under Swiss AMLA art. 305bis / VQF AML Policy s5.5</div>
        </div>

        {/* Documents */}
        <div style={S.section}>
          <div style={S.sectionTitle}>7. Documents on File ({documents.length})</div>
          {documents.length === 0 ? <div style={{ ...S.para, color: "#9ca3af" }}>No documents uploaded</div> : (
            <div>
              {documents.map((doc, i) => (
                <div key={i} style={{ padding:"12px 14px", borderRadius:6, border:"1px solid #e5e7eb", marginBottom:8, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif", marginBottom:2 }}>{doc.document_name}</div>
                      {doc.document_type && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginBottom: doc.analysis ? 6 : 0 }}>{doc.document_type}</div>}
                      {(() => {
                        // Parse analysis whether stored as object or JSON string
                        let a = doc.analysis;
                        if (typeof a === "string") { try { a = JSON.parse(a); } catch(e) { a = null; } }
                        if (!a) return null;
                        const findings = a.keyFindings || [];
                        const materialFlags = [...(a.materialFlags||[]), ...(a.redFlags||[])];
                        const notedFlags = a.notedFlags || [];
                        const informationals = a.informationals || [];
                        return (
                          <div style={{ paddingLeft:8, borderLeft:"3px solid #e5e7eb" }}>
                            {findings.length > 0 && findings.map((f, fi) => (
                              <div key={fi} style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:2 }}>— {toStr(f)}</div>
                            ))}
                            {/* Material flags — red, require action or documented mitigation */}
                            {materialFlags.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#fff1f2", border:"1px solid #fecaca", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#991b1b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>🔴 Material — Action Required</div>
                                {materialFlags.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#7f1d1d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {toStr(f)}</div>
                                ))}
                              </div>
                            )}
                            {/* Noted flags — amber, standard for client type */}
                            {notedFlags.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#92400e", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>🟡 Noted — Standard for Client Type</div>
                                {notedFlags.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#78350f", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {toStr(f)}</div>
                                ))}
                              </div>
                            )}
                            {/* Informationals — blue, factual context only */}
                            {informationals.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#1e40af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>ℹ️ Informational</div>
                                {informationals.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#1e3a8a", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {toStr(f)}</div>
                                ))}
                              </div>
                            )}
                            {/* MLRO mitigation note — always shown when present, always retained in audit trail */}
                            {doc.mlro_note && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:3 }}>✓ MLRO Mitigation Note</div>
                                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.5 }}>{doc.mlro_note}</div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                      <span style={S.badge(doc.assessment === "Satisfactory" ? "#15803d" : doc.assessment === "Requires Clarification" ? "#d97706" : "#6b7280")}>
                        {doc.assessment || "Received"}
                      </span>
                      {doc.file_path ? (
                        <button onClick={() => getSignedUrl(doc.file_path)} style={{ fontSize:11, color:"#1e2d5a", background:"transparent", border:"1px solid #1e2d5a", borderRadius:4, padding:"3px 10px", cursor:"pointer", fontFamily:"Arial, sans-serif" }}>View</button>
                      ) : <span style={{ fontSize:11, color:"#d1d5db" }}>—</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MLRO Sign-off statement */}
        <div style={S.section}>
          <div style={S.sectionTitle}>8. MLRO Internal Review &amp; Sign-off</div>
          <div style={{ padding:"14px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8 }}>
            <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              This file has been reviewed by the internal MLRO at Sage Capital Management. All risk factors identified during the onboarding process have been assessed, mitigated where appropriate, and signed off internally prior to submission for external compliance review.
            </div>
            {session?.mlro_notes && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #bbf7d0" }}>
                <div style={{ fontSize:11, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>MLRO Notes</div>
                <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{session.mlro_notes}</div>
              </div>
            )}
            <div style={{ marginTop:10, fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif" }}>
              Approved by: {session?.mlro_decision ? session.mlro_decision + " — " : ""}{new Date(session?.approved_at || "").toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })}
            </div>
          </div>
        </div>

        {/* Risk Factors & Mitigations */}
        {(report.riskFactors?.length > 0) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>9. Risk Assessment &amp; Mitigants</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:10 }}>
              The following risk factors were identified during the onboarding process. Each has been assessed by the internal MLRO and mitigating controls identified. This assessment informed the decision to approve this client relationship.
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#fff1f2", border:"1px solid #fecaca", color:"#991b1b", fontFamily:"Arial, sans-serif", fontWeight:600 }}>🔴 Material — requires action or documented mitigation</span>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#fff7ed", border:"1px solid #fed7aa", color:"#92400e", fontFamily:"Arial, sans-serif", fontWeight:600 }}>🟡 Noted — standard for client type, on file</span>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#eff6ff", border:"1px solid #bfdbfe", color:"#1e40af", fontFamily:"Arial, sans-serif", fontWeight:600 }}>ℹ️ Informational — context only</span>
            </div>
            {report.riskFactors.map((f, i) => {
              const factor = typeof f === "string" ? f : toStr(f.factor);
              const notes = typeof f === "object" ? toStr(f.notes) : null;
              const rating = typeof f === "object" ? (typeof f.rating === "string" ? f.rating : toStr(f.rating)) : null;
              const ratingColor = rating === "High" ? "#dc2626" : rating === "Medium" ? "#d97706" : "#15803d";
              const bg = rating === "High" ? "#fff1f2" : rating === "Medium" ? "#fff7ed" : "#f0fdf4";
              const border = rating === "High" ? "#fecaca" : rating === "Medium" ? "#fed7aa" : "#bbf7d0";
              return (
                <div key={i} style={{ marginBottom:8, padding:"12px 14px", background:bg, borderRadius:6, border:`1px solid ${border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom: notes ? 6 : 0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif" }}>{factor}</div>
                    {rating && <span style={{ fontSize:10, fontWeight:700, color:ratingColor, whiteSpace:"nowrap", padding:"2px 8px", borderRadius:4, background:`${ratingColor}18`, border:`1px solid ${ratingColor}40`, flexShrink:0 }}>{rating}</span>}
                  </div>
                  {notes && <div style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{notes}</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Travel Rule Detail */}
        {report.travelRule?.wallets?.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>8. Travel Rule — FINMA Guidance 02/2019</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:14 }}>
              The following table documents Travel Rule compliance for each virtual asset wallet associated with this client, as required under Swiss FINMA Guidance 02/2019 and VQF art. 13-14.
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden" }}>
              <thead>
                <tr style={{ background:"#1e2d5a" }}>
                  {["Wallet Address","Type","Exchange / Method","Status"].map(h => (
                    <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, color:"white", fontFamily:"Arial, sans-serif", fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.travelRule.wallets.map((w, i) => (
                  <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f9fafb", borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"8px 10px", fontSize:11, color:"#374151", fontFamily:"monospace" }}>{w.address?.slice(0,16)}...{w.address?.slice(-6)}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", textTransform:"capitalize" }}>{w.type || "—"}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif" }}>
                      {w.type === "exchange" ? `${w.exchangeName || "—"} ${w.exchangeRegulated ? "· Regulated" : "· Unregulated"} ${w.accountInEntityName ? "· Account verified" : ""}` :
                       w.type === "private" ? `Proof of control: ${w.proofOfControl?.method === "satoshi_test_complete" ? `Satoshi test verified ${w.proofOfControl.verifiedDate ? new Date(w.proofOfControl.verifiedDate).toLocaleDateString("en-GB") : ""}` : "Pending"}` : "—"}
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      <span style={S.badge(w.travelRuleStatus === "compliant" ? "#15803d" : "#d97706")}>
                        {w.travelRuleStatus === "compliant" ? "✓ Compliant" : w.travelRuleStatus === "pending_satoshi_test" ? "Satoshi pending" : w.travelRuleStatus === "pending_client_response" ? "Awaiting client" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Director Decision Panel */}
        <div style={{ ...S.section, border: "2px solid #2abfb0" }}>
          <div style={{ ...S.sectionTitle, color: "#0f766e", borderBottomColor: "#2abfb0" }}>Director Review — Your Decision</div>
          <div style={{ ...S.para, marginBottom: 16 }}>
            Having reviewed the compliance file prepared by Ross Griffiths, please choose one of the options below. Your decision will be immediately logged and the compliance team notified.
          </div>

          <div style={{ padding:"12px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              <strong>Send to Jaybee</strong> — submits the file directly to Jaybee Compliance for their independent external sign-off. Jaybee will receive a secure review link by email.<br/>
              <strong>Return to Ross</strong> — sends the file back to Ross with your notes. He will be notified by email and the file will return to Pending Review status.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Notes (required if returning to Ross)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any comments, conditions, or instructions for Ross..." style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", resize: "vertical", minHeight: 80, outline: "none", boxSizing: "border-box" }} />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => submitDecision("approve")} disabled={submitting} style={{ ...S.btn("#2abfb0", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "Submitting..." : "✓ Send to Jaybee →"}
            </button>
            <button onClick={() => submitDecision("info")} disabled={submitting} style={{ ...S.btn("#d97706", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              ← Return to Ross
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "Arial, sans-serif", fontSize: 11, color: "#9ca3af" }}>
          Confidential — Director Review · Sage Capital Management · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
